import React, { useEffect, useState, useRef } from "react";
import { Save, KeyRound, Lock, Video, Upload, X, Coins, ToggleLeft, ToggleRight, RefreshCcw, Instagram, Facebook, MessageCircle, Users, Youtube, Twitter, Music2, Plus, Trash2, HelpCircle, Share2 } from "lucide-react";
import api from "../../lib/api";
import { uploadVideoChunked } from "../../lib/uploadVideo";
import { useApp } from "../../context/AppContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_FIELDS = [
    { key: "instagram_url", label: "Instagram URL", icon: Instagram, placeholder: "https://instagram.com/asishotels" },
    { key: "facebook_url", label: "Facebook Sayfa URL", icon: Facebook, placeholder: "https://facebook.com/asishotels" },
    { key: "facebook_group_url", label: "Facebook Grup URL", icon: Users, placeholder: "https://facebook.com/groups/..." },
    { key: "whatsapp_group_url", label: "WhatsApp Grup Davet Linki", icon: MessageCircle, placeholder: "https://chat.whatsapp.com/..." },
    { key: "youtube_url", label: "YouTube URL", icon: Youtube, placeholder: "https://youtube.com/@asishotels" },
    { key: "twitter_url", label: "X (Twitter) URL", icon: Twitter, placeholder: "https://x.com/asishotels" },
    { key: "tiktok_url", label: "TikTok URL", icon: Music2, placeholder: "https://tiktok.com/@asishotels" },
];

const AdminSettings = () => {
    const { t, loadSettings, admin, logout, rates, loadRates } = useApp();
    const [data, setData] = useState({
        iban: "",
        bank_name: "",
        account_holder: "",
        whatsapp_number: "",
        company_name: "",
        contact_email: "",
        deposit_percent: 10,
        no_meal_discount: 0,
        promo_video_url: "",
        manual_rates_enabled: false,
        manual_usd_try: 0,
        manual_sar_try: 0,
        instagram_url: "",
        facebook_url: "",
        facebook_group_url: "",
        whatsapp_group_url: "",
        youtube_url: "",
        twitter_url: "",
        tiktok_url: "",
        extra_contacts: [],
        wa_template_approved: "",
        wa_template_rejected: "",
        wa_template_new_booking: "",
        faqs: [],
    });
    const [pw, setPw] = useState({ old_password: "", new_password: "", confirm: "" });
    const [pwLoading, setPwLoading] = useState(false);
    const [videoUploading, setVideoUploading] = useState(false);
    const [videoProgress, setVideoProgress] = useState(0);
    const videoInputRef = useRef(null);

    useEffect(() => {
        api.get("/settings").then(({ data }) => setData((d) => ({ ...d, ...data, faqs: data?.faqs || [] })));
    }, []);

    const handleChange = (k, v) => setData({ ...data, [k]: v });

    const addFaq = () => {
        const newId = `f${Date.now()}`;
        setData({ ...data, faqs: [...(data.faqs || []), { id: newId, question: "", answer: "" }] });
    };
    const updateFaq = (id, field, value) => {
        setData({ ...data, faqs: data.faqs.map((f) => f.id === id ? { ...f, [field]: value } : f) });
    };
    const removeFaq = (id) => {
        setData({ ...data, faqs: data.faqs.filter((f) => f.id !== id) });
    };

    const save = async (partialUpdate = {}) => {
        const payload = {
            ...data,
            ...partialUpdate,
            deposit_percent: Number(data.deposit_percent) || 10,
            manual_usd_try: Number(data.manual_usd_try) || 0,
            manual_sar_try: Number(data.manual_sar_try) || 0,
        };
        try {
            await api.put("/admin/settings", payload);
            toast.success("Ayarlar kaydedildi");
            loadSettings();
            loadRates();
        } catch (e) {
            toast.error("Hata: " + (e.response?.data?.detail || ""));
        }
    };

    const handleVideoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 300 * 1024 * 1024) {
            toast.error("Video 300MB'tan büyük olamaz");
            return;
        }
        setVideoUploading(true);
        setVideoProgress(0);
        try {
            const resp = await uploadVideoChunked(file, setVideoProgress);
            const fullUrl = `${BACKEND_URL}${resp.url}`;
            const newData = { ...data, promo_video_url: fullUrl };
            setData(newData);
            // Persist immediately
            await api.put("/admin/settings", {
                ...newData,
                deposit_percent: Number(newData.deposit_percent) || 10,
                manual_usd_try: Number(newData.manual_usd_try) || 0,
                manual_sar_try: Number(newData.manual_sar_try) || 0,
            });
            loadSettings();
            toast.success("Tanıtım videosu yüklendi ve yayınlandı");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Video yüklenemedi");
        } finally {
            setVideoUploading(false);
            setVideoProgress(0);
            if (videoInputRef.current) videoInputRef.current.value = "";
        }
    };

    const removeVideo = async () => {
        if (!confirm("Tanıtım videosunu kaldırmak istediğinize emin misiniz?")) return;
        setData({ ...data, promo_video_url: "" });
        await save({ promo_video_url: "" });
    };

    const toggleManual = async () => {
        const next = !data.manual_rates_enabled;
        let updated = { ...data, manual_rates_enabled: next };
        // If enabling for the first time, prefill from current live rates
        if (next && (!data.manual_usd_try || !data.manual_sar_try) && rates?.raw) {
            updated.manual_usd_try = rates.raw.USD_TRY || 0;
            updated.manual_sar_try = rates.raw.SAR_TRY || 0;
        }
        setData(updated);
        try {
            await api.put("/admin/settings", {
                ...updated,
                deposit_percent: Number(updated.deposit_percent) || 10,
                manual_usd_try: Number(updated.manual_usd_try) || 0,
                manual_sar_try: Number(updated.manual_sar_try) || 0,
            });
            toast.success(next ? "Manuel kur etkinleştirildi" : "Canlı kura geri dönüldü");
            loadSettings();
            loadRates();
        } catch (e) {
            toast.error("Hata: " + (e.response?.data?.detail || ""));
        }
    };

    const changePassword = async (e) => {
        e.preventDefault();
        if (pw.new_password.length < 6) { toast.error("Yeni şifre en az 6 karakter olmalı"); return; }
        if (pw.new_password !== pw.confirm) { toast.error("Yeni şifre ve onay eşleşmiyor"); return; }
        setPwLoading(true);
        try {
            await api.post("/auth/change-password", { old_password: pw.old_password, new_password: pw.new_password });
            toast.success("Şifre değiştirildi. Yeni şifrenizle giriş yapın.");
            setPw({ old_password: "", new_password: "", confirm: "" });
            setTimeout(() => { logout(); window.location.href = "/admin/login"; }, 1200);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Şifre değiştirilemedi");
        } finally { setPwLoading(false); }
    };

    const generalFields = [
        ["company_name", "Şirket Adı"],
        ["contact_email", "İletişim E-posta"],
        ["whatsapp_number", "WhatsApp Numarası (örn: 905555555555)"],
        ["iban", "IBAN"],
        ["bank_name", "Banka Adı"],
        ["account_holder", "Hesap Sahibi"],
    ];

    return (
        <div className="max-w-4xl">
            <h1 className="font-serif text-4xl mb-2">{t("admin.settings")}</h1>
            <p className="text-muted_green mb-8">Genel ayarlar, kur yönetimi, tanıtım videosu ve hesap güvenliği</p>

            {/* TANITIM VİDEOSU */}
            <div className="border border-border_green bg-emerald_surface p-7 space-y-5 mb-6">
                <div className="text-xs uppercase tracking-[0.25em] text-gold border-b border-border_green pb-3 flex items-center gap-2">
                    <Video className="w-4 h-4" /> Şirket Tanıtım Videosu
                </div>

                {data.promo_video_url ? (
                    <div className="space-y-3">
                        <div className="border border-border_green bg-emerald_deep">
                            <video
                                src={data.promo_video_url}
                                controls
                                className="w-full max-h-[280px] object-contain"
                                data-testid="promo-video-preview"
                            />
                        </div>
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <div className="text-xs text-muted_green break-all">
                                Yayında · ana sayfada gösteriliyor
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => videoInputRef.current?.click()}
                                    disabled={videoUploading}
                                    className="btn-outline-gold !text-xs !py-2"
                                    data-testid="replace-video-btn"
                                >
                                    <Upload className="w-3.5 h-3.5" /> {videoUploading ? `Yükleniyor… %${videoProgress}` : "Değiştir"}
                                </button>
                                <button
                                    onClick={removeVideo}
                                    className="btn-outline-gold !text-xs !py-2 !text-red-400 !border-red-500/40"
                                    data-testid="remove-video-btn"
                                >
                                    <X className="w-3.5 h-3.5" /> Kaldır
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        onClick={() => videoInputRef.current?.click()}
                        className="border-2 border-dashed border-border_green hover:border-gold transition-colors cursor-pointer p-12 text-center"
                        data-testid="video-drop-zone"
                    >
                        <Video className="w-10 h-10 text-gold mx-auto mb-3 opacity-50" />
                        <div className="font-serif text-xl mb-2">Tanıtım Videosu Yükle</div>
                        <div className="text-xs text-muted_green mb-4">
                            MP4 veya WEBM · Maksimum 300 MB · 16:9 önerilir
                        </div>
                        <button
                            type="button"
                            disabled={videoUploading}
                            className="btn-gold !text-xs"
                            data-testid="upload-video-btn"
                        >
                            <Upload className="w-4 h-4" /> {videoUploading ? `Yükleniyor… %${videoProgress}` : "Bilgisayardan Seç"}
                        </button>
                    </div>
                )}
                <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    onChange={handleVideoUpload}
                    className="hidden"
                    data-testid="video-file-input"
                />
            </div>

            {/* KUR AYARI */}
            <div className="border border-border_green bg-emerald_surface p-7 space-y-5 mb-6">
                <div className="text-xs uppercase tracking-[0.25em] text-gold border-b border-border_green pb-3 flex items-center gap-2">
                    <Coins className="w-4 h-4" /> Kur Yönetimi
                </div>

                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                        <div className="text-sm font-semibold mb-1">Manuel Kur Sabitle</div>
                        <div className="text-xs text-muted_green">
                            Açıldığında canlı Kuveyt Türk kuru yerine aşağıda belirttiğiniz değerler kullanılır.
                        </div>
                    </div>
                    <button
                        onClick={toggleManual}
                        className="flex items-center gap-2 px-3 py-1.5 border border-border_green hover:border-gold transition-colors"
                        data-testid="manual-rates-toggle"
                    >
                        {data.manual_rates_enabled ? (
                            <>
                                <ToggleRight className="w-7 h-7 text-gold" />
                                <span className="text-xs uppercase tracking-[0.2em] text-gold font-bold">Manuel</span>
                            </>
                        ) : (
                            <>
                                <ToggleLeft className="w-7 h-7 text-muted_green" />
                                <span className="text-xs uppercase tracking-[0.2em] text-muted_green">Canlı</span>
                            </>
                        )}
                    </button>
                </div>

                <div className={`grid sm:grid-cols-2 gap-4 ${data.manual_rates_enabled ? "" : "opacity-50 pointer-events-none"}`}>
                    <div>
                        <label className="label-dark">1 USD = ? TRY</label>
                        <input
                            type="number"
                            step="0.0001"
                            min={0}
                            className="input-dark"
                            value={data.manual_usd_try}
                            onChange={(e) => handleChange("manual_usd_try", e.target.value)}
                            placeholder="örn: 48.50"
                            data-testid="manual-usd-try"
                        />
                    </div>
                    <div>
                        <label className="label-dark">1 SAR = ? TRY</label>
                        <input
                            type="number"
                            step="0.0001"
                            min={0}
                            className="input-dark"
                            value={data.manual_sar_try}
                            onChange={(e) => handleChange("manual_sar_try", e.target.value)}
                            placeholder="örn: 12.90"
                            data-testid="manual-sar-try"
                        />
                    </div>
                </div>

                <div className="bg-emerald_deep/50 border border-border_green p-4 text-xs">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-gold uppercase tracking-[0.2em] text-[10px]">Şu anda etkin olan kur</div>
                        <button onClick={loadRates} className="text-muted_green hover:text-gold flex items-center gap-1" title="Yenile">
                            <RefreshCcw className="w-3 h-3" /> Yenile
                        </button>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-2">
                        <div><span className="text-muted_green">USD/TRY:</span><div className="text-gold font-mono">{rates?.raw?.USD_TRY?.toFixed(4) || "—"}</div></div>
                        <div><span className="text-muted_green">SAR/TRY:</span><div className="text-gold font-mono">{rates?.raw?.SAR_TRY?.toFixed(4) || "—"}</div></div>
                        <div><span className="text-muted_green">SAR/USD:</span><div className="text-gold font-mono">{rates?.rates?.USD?.toFixed(5) || "—"}</div></div>
                    </div>
                    <div className="text-muted_green">
                        Kaynak: <span className={rates?.manual ? "text-gold" : ""}>{rates?.source || "—"}</span>
                    </div>
                </div>

                {data.manual_rates_enabled && (
                    <button onClick={() => save()} className="btn-gold" data-testid="save-manual-rates">
                        <Save className="w-4 h-4" /> Manuel Kuru Kaydet
                    </button>
                )}
            </div>

            {/* SOSYAL MEDYA */}
            <div className="border border-border_green bg-emerald_surface p-7 space-y-5 mb-6">
                <div className="text-xs uppercase tracking-[0.25em] text-gold border-b border-border_green pb-3 flex items-center gap-2">
                    <Share2 className="w-4 h-4" /> Sosyal Medya & Topluluk Linkleri
                </div>
                <p className="text-xs text-muted_green -mt-2">
                    Bu linkler ana sayfada ve otel detay sayfalarındaki "İletişim & Sosyal" sekmesinde görünür. Boş bırakılan kanallar gösterilmez.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                    {SOCIAL_FIELDS.map(({ key, label, icon: Icon, placeholder }) => (
                        <div key={key}>
                            <label className="label-dark flex items-center gap-2">
                                <Icon className="w-3.5 h-3.5 text-gold" /> {label}
                            </label>
                            <input
                                type="url"
                                className="input-dark"
                                value={data[key] || ""}
                                onChange={(e) => handleChange(key, e.target.value)}
                                placeholder={placeholder}
                                data-testid={`settings-${key}`}
                            />
                        </div>
                    ))}
                </div>
                <button onClick={() => save()} className="btn-gold" data-testid="save-social-btn">
                    <Save className="w-4 h-4" /> Sosyal Linkleri Kaydet
                </button>
            </div>

            {/* SSS */}
            <div className="border border-border_green bg-emerald_surface p-7 space-y-5 mb-6">
                <div className="text-xs uppercase tracking-[0.25em] text-gold border-b border-border_green pb-3 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4" /> Sıkça Sorulan Sorular
                </div>
                <p className="text-xs text-muted_green -mt-2">
                    Buraya eklenen sorular ana sayfadaki SSS bölümünde ve otel detay sayfasının "SSS" sekmesinde gösterilir.
                </p>
                {(!data.faqs || data.faqs.length === 0) ? (
                    <div className="text-sm text-muted_green border border-dashed border-border_green p-6 text-center">
                        Henüz SSS eklenmedi. Aşağıdan ekleyebilirsiniz.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {data.faqs.map((f, idx) => (
                            <div key={f.id} className="border border-border_green bg-emerald_deep/40 p-4 space-y-3" data-testid={`faq-editor-${f.id}`}>
                                <div className="flex items-center justify-between">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-gold">Soru #{idx + 1}</div>
                                    <button
                                        onClick={() => removeFaq(f.id)}
                                        className="text-red-400 hover:text-red-500 text-xs flex items-center gap-1"
                                        data-testid={`faq-remove-${f.id}`}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" /> Sil
                                    </button>
                                </div>
                                <input
                                    type="text"
                                    className="input-dark"
                                    placeholder="Soru... (Türkçe)"
                                    value={f.question}
                                    onChange={(e) => updateFaq(f.id, "question", e.target.value)}
                                    data-testid={`faq-question-${f.id}`}
                                />
                                <textarea
                                    className="input-dark"
                                    rows={3}
                                    placeholder="Cevap... (Türkçe)"
                                    value={f.answer}
                                    onChange={(e) => updateFaq(f.id, "answer", e.target.value)}
                                    data-testid={`faq-answer-${f.id}`}
                                />
                                <div className="pt-2 border-t border-border_green/50 space-y-3">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted_green">🇬🇧 İngilizce (opsiyonel — boşsa Türkçesi gösterilir)</div>
                                    <input
                                        type="text"
                                        className="input-dark"
                                        placeholder="Question... (English)"
                                        value={f.question_en || ""}
                                        onChange={(e) => updateFaq(f.id, "question_en", e.target.value)}
                                        data-testid={`faq-question-en-${f.id}`}
                                    />
                                    <textarea
                                        className="input-dark"
                                        rows={2}
                                        placeholder="Answer... (English)"
                                        value={f.answer_en || ""}
                                        onChange={(e) => updateFaq(f.id, "answer_en", e.target.value)}
                                        data-testid={`faq-answer-en-${f.id}`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex gap-3 flex-wrap">
                    <button onClick={addFaq} className="btn-outline-gold !text-xs" data-testid="add-faq-btn">
                        <Plus className="w-3.5 h-3.5" /> Yeni Soru Ekle
                    </button>
                    <button onClick={() => save()} className="btn-gold !text-xs" data-testid="save-faqs-btn">
                        <Save className="w-3.5 h-3.5" /> SSS'leri Kaydet
                    </button>
                </div>
            </div>

            {/* EK İLETİŞİM ALANLARI */}
            <div className="border border-border_green bg-emerald_surface p-7 space-y-4 mb-6">
                <div className="text-xs uppercase tracking-[0.25em] text-gold border-b border-border_green pb-3 flex items-center gap-2">
                    <Share2 className="w-4 h-4" /> Ek İletişim Bilgileri
                </div>
                <p className="text-xs text-muted_green -mt-2">
                    Birden fazla telefon, ek e-posta, ofis adresleri veya özel kontaklar ekleyin. Bu bilgiler İletişim sayfasında ve otel detay "İletişim & Sosyal" sekmesinde görünür.
                </p>
                {(data.extra_contacts || []).map((c, i) => (
                    <div key={i} className="grid sm:grid-cols-12 gap-2 items-end border-l-2 border-gold/40 pl-3" data-testid={`extra-contact-${i}`}>
                        <div className="sm:col-span-3">
                            <label className="label-dark">Etiket</label>
                            <input className="input-dark" value={c.label || ""} onChange={(e) => {
                                const next = [...data.extra_contacts]; next[i] = { ...next[i], label: e.target.value }; handleChange("extra_contacts", next);
                            }} placeholder="Satış Hattı, Genel Müdür..." />
                        </div>
                        <div className="sm:col-span-3">
                            <label className="label-dark">Tip</label>
                            <select className="input-dark" value={c.type || "phone"} onChange={(e) => {
                                const next = [...data.extra_contacts]; next[i] = { ...next[i], type: e.target.value }; handleChange("extra_contacts", next);
                            }}>
                                <option value="phone" className="bg-emerald_surface">Telefon</option>
                                <option value="whatsapp" className="bg-emerald_surface">WhatsApp</option>
                                <option value="email" className="bg-emerald_surface">E-posta</option>
                                <option value="address" className="bg-emerald_surface">Adres</option>
                                <option value="link" className="bg-emerald_surface">Web Linki</option>
                            </select>
                        </div>
                        <div className="sm:col-span-5">
                            <label className="label-dark">Değer</label>
                            <input className="input-dark" value={c.value || ""} onChange={(e) => {
                                const next = [...data.extra_contacts]; next[i] = { ...next[i], value: e.target.value }; handleChange("extra_contacts", next);
                            }} placeholder="+90 532..., info@..., Cidde Yolu..." />
                        </div>
                        <button onClick={() => handleChange("extra_contacts", data.extra_contacts.filter((_, j) => j !== i))} className="sm:col-span-1 text-rose-400 hover:text-rose-500 text-xs uppercase tracking-[0.15em]">Sil</button>
                    </div>
                ))}
                <div className="flex gap-2 flex-wrap">
                    <button onClick={() => handleChange("extra_contacts", [...(data.extra_contacts || []), { label: "", value: "", type: "phone" }])} className="btn-outline-gold !text-xs" data-testid="add-extra-contact">
                        <Plus className="w-3.5 h-3.5" /> Yeni İletişim Ekle
                    </button>
                    <button onClick={() => save()} className="btn-gold !text-xs" data-testid="save-extra-contacts">
                        <Save className="w-3.5 h-3.5" /> Kaydet
                    </button>
                </div>
            </div>

            {/* WHATSAPP ŞABLONLARI */}
            <div className="border border-border_green bg-emerald_surface p-7 space-y-5 mb-6">
                <div className="text-xs uppercase tracking-[0.25em] text-gold border-b border-border_green pb-3 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" /> WhatsApp Mesaj Şablonları
                </div>
                <p className="text-xs text-muted_green -mt-2">
                    Müşterilere otomatik gönderilen mesajların metnini özelleştirin. Kullanılabilir değişkenler:{" "}
                    <span className="text-gold">{"{name}"}, {"{code}"}, {"{hotel}"}, {"{check_in}"}, {"{check_out}"}, {"{nights}"}, {"{total}"}, {"{deposit}"}, {"{remaining}"}, {"{profile_url}"}, {"{iban}"}, {"{company}"}, {"{reason}"}</span>
                </p>
                <div>
                    <label className="label-dark">Rezervasyon Onay Mesajı</label>
                    <textarea
                        rows={6}
                        className="input-dark font-mono text-xs"
                        value={data.wa_template_approved || ""}
                        onChange={(e) => handleChange("wa_template_approved", e.target.value)}
                        placeholder={`Sayın {name},\n\n{hotel} otelindeki rezervasyonunuz onaylanmıştır. ✅\n\nRezervasyon kodu: {code}\nGiriş: {check_in}\nÇıkış: {check_out} ({nights} gece)\nToplam tutar: {total}\nÖn ödeme tutarı: {deposit}\n\nIBAN: {iban}\n\n— {company}`}
                        data-testid="wa-template-approved"
                    />
                </div>
                <div>
                    <label className="label-dark">Rezervasyon Red Mesajı</label>
                    <textarea
                        rows={5}
                        className="input-dark font-mono text-xs"
                        value={data.wa_template_rejected || ""}
                        onChange={(e) => handleChange("wa_template_rejected", e.target.value)}
                        placeholder={`Sayın {name},\n\nMaalesef {hotel} için talebiniz şu sebeple reddedildi: {reason}\n\nFarklı tarihler veya başka bir otel için tekrar talep oluşturabilirsiniz.\n\n— {company}`}
                        data-testid="wa-template-rejected"
                    />
                </div>
                <div>
                    <label className="label-dark">Yeni Rezervasyon Bildirimi (Müşteriye)</label>
                    <textarea
                        rows={5}
                        className="input-dark font-mono text-xs"
                        value={data.wa_template_new_booking || ""}
                        onChange={(e) => handleChange("wa_template_new_booking", e.target.value)}
                        placeholder={`Sayın {name}, talebiniz alındı.\n\nRezervasyon kodu: {code}\nDurum: Onay bekliyor\n\nKısa süre içinde size dönüş yapacağız.\n\n— {company}`}
                        data-testid="wa-template-new-booking"
                    />
                </div>
                <button onClick={() => save()} className="btn-gold" data-testid="save-wa-templates-btn">
                    <Save className="w-4 h-4" /> WhatsApp Şablonlarını Kaydet
                </button>
            </div>

            {/* GENEL AYARLAR */}
            <div className="border border-border_green bg-emerald_surface p-7 space-y-5 mb-6">
                <div className="text-xs uppercase tracking-[0.25em] text-gold border-b border-border_green pb-3">
                    Genel Ayarlar
                </div>
                {generalFields.map(([k, l]) => (
                    <div key={k}>
                        <label className="label-dark">{l}</label>
                        <input className="input-dark" value={data[k] || ""} onChange={(e) => handleChange(k, e.target.value)} data-testid={`settings-${k}`} />
                    </div>
                ))}
                <div>
                    <label className="label-dark">Depozito Yüzdesi (%)</label>
                    <input type="number" min={0} max={100} className="input-dark max-w-xs" value={data.deposit_percent} onChange={(e) => handleChange("deposit_percent", e.target.value)} data-testid="settings-deposit" />
                </div>
                <div>
                    <label className="label-dark">Yemeksiz İndirimi (SAR — kişi başı/gece)</label>
                    <input type="number" min={0} step="any" className="input-dark max-w-xs" value={data.no_meal_discount ?? 0} onChange={(e) => handleChange("no_meal_discount", e.target.value)} data-testid="settings-no-meal-discount" />
                    <div className="text-[10px] text-muted_green mt-1">Müşteri &ldquo;Yemek Yok&rdquo; seçtiğinde bu tutar kişi başı × gece kadar toplamdan düşülür.</div>
                </div>
                <button onClick={() => save()} className="btn-gold" data-testid="save-settings-btn">
                    <Save className="w-4 h-4" /> Kaydet
                </button>
            </div>

            {/* ŞİFRE */}
            <form onSubmit={changePassword} className="border border-border_green bg-emerald_surface p-7 space-y-5">
                <div className="text-xs uppercase tracking-[0.25em] text-gold border-b border-border_green pb-3 flex items-center gap-2">
                    <KeyRound className="w-3.5 h-3.5" /> Şifre Değiştir
                </div>
                <div className="bg-emerald_deep/50 border border-border_green p-4 text-sm flex items-center gap-3">
                    <Lock className="w-4 h-4 text-gold flex-shrink-0" />
                    <div>
                        <div className="text-xs text-muted_green">Mevcut kullanıcı</div>
                        <div className="text-gold font-mono">{admin?.email}</div>
                    </div>
                </div>
                <div>
                    <label className="label-dark">Mevcut Şifre</label>
                    <input type="password" required value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} className="input-dark" data-testid="pw-old" autoComplete="current-password" />
                </div>
                <div>
                    <label className="label-dark">Yeni Şifre (en az 6 karakter)</label>
                    <input type="password" required minLength={6} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="input-dark" data-testid="pw-new" autoComplete="new-password" />
                </div>
                <div>
                    <label className="label-dark">Yeni Şifre (Tekrar)</label>
                    <input type="password" required value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="input-dark" data-testid="pw-confirm" autoComplete="new-password" />
                </div>
                <button type="submit" disabled={pwLoading} className="btn-gold" data-testid="change-password-btn">
                    <KeyRound className="w-4 h-4" /> {pwLoading ? "..." : "Şifreyi Değiştir"}
                </button>
                <div className="text-[11px] text-muted_green">
                    Şifre değiştirildikten sonra otomatik çıkış yapılacaktır.
                </div>
            </form>
        </div>
    );
};

export default AdminSettings;
